import React, { useEffect, useState, useCallback } from "react";
import api from "../api/api";
import Layout from "../components/Layout";
import InterviewTable from "../components/InterviewTable";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { parseISO } from "date-fns";
import ProfileCard from "../components/ProfileCard";


import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Briefcase, Layers, HelpCircle, Bell, Filter } from "lucide-react";
import TimelineView from "../components/TimelineView";
import { fetchInterviewTimeline } from "../utils/timelineClient";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { connectSocket, getSocket } from "../utils/socket";
import ResumeUpload from "../components/ResumeUpload";
import SubscribeButton from './SubscribeButton';



ChartJS.register(ArcElement, Tooltip, Legend);

function decodeJWT(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload;
  } catch (err) {
    console.error("Failed to decode JWT:", err);
    return null;
  }
}

function InterviewBoard() {
  const [interviews, setInterviews] = useState([]);
  const [timelines, setTimelines] = useState({});
  const [stats, setStats] = useState({
    totalInterviews: 0,
    totalRounds: 0,
    totalQuestions: 0,
    interviewsByCompany: {},
  });
  const [userData, setUserData] = useState({ name: "", email: "" });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filters, setFilters] = useState({
    company: "",
    status: "",
    startDate: "",
    endDate: "",
  });
  const [calendarEvents, setCalendarEvents] = useState([]);
  

  const fetchData = useCallback(
    async (applyFilters = false) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("You must be logged in to view the dashboard.");
          setLoading(false);
          return;
        }

        const decoded = decodeJWT(token);
        const userId = decoded?.id || decoded?.user_id || decoded?.sub;

        if (!userId) {
          setError("Invalid token: user ID not found.");
          setLoading(false);
          return;
        }

        if (decoded?.name && decoded?.email) {
          setUserData({
            name: decoded.name,
            email: decoded.email,
          });
        }

        const headers = { Authorization: `Bearer ${token}` };

        // WebSocket connection
        const socket = connectSocket(userId, token);
        socket.off("notification");
        socket.on("notification", (data) => {
          toast.info(`${data.title}: ${data.message}`, {
            position: "top-right",
            autoClose: 5000,
            theme: "colored",
          });
          setNotifications((prev) => [data, ...prev]);
          setUnreadCount((prev) => prev + 1);
        });

        // Apply filters
        let url = "/interviews";
        if (applyFilters) {
          const params = [];
          if (filters.company) params.push(`company=${filters.company}`);
          if (filters.status) params.push(`status=${filters.status}`);
          if (filters.startDate) params.push(`startDate=${filters.startDate}`);
          if (filters.endDate) params.push(`endDate=${filters.endDate}`);
          if (params.length > 0) url += `?${params.join("&")}`;
        }

        const interviewsRes = await api.get(url, { headers });
        const interviewsData =
          interviewsRes.data.interviews || interviewsRes.data;
        setInterviews(interviewsData);

        // Fetch timeline per interview
        interviewsData.forEach(async (interview) => {
          try {
            const timeline = await fetchInterviewTimeline(interview.id);
            setTimelines((prev) => ({ ...prev, [interview.id]: timeline }));
          } catch (e) {
            console.error(
              `Failed to load timeline for interview ${interview.id}`,
              e
            );
          }
        });

        // Stats
        let totalRounds = 0;
        let totalQuestions = 0;
        const interviewsByCompany = {};

        for (const interview of interviewsData) {
          interviewsByCompany[interview.company] =
            (interviewsByCompany[interview.company] || 0) + 1;

          const roundsRes = await api.get(
            `/interviews/${interview.id}/rounds`,
            { headers }
          );
          const rounds = roundsRes.data.rounds || roundsRes.data;
          totalRounds += rounds.length;

          for (const round of rounds) {
            const questionsRes = await api.get(
              `/rounds/${round.id}/questions`,
              { headers }
            );
            const questions =
              questionsRes.data.questions || questionsRes.data;
            totalQuestions += questions.length;
          }
        }

        setStats({
          totalInterviews: interviewsData.length,
          totalRounds,
          totalQuestions,
          interviewsByCompany,
        });
        setError(null);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    fetchData();
    return () => {
      const socket = getSocket();
      if (socket && socket.connected) {
        socket.disconnect();
        console.log("🔌 WebSocket disconnected on unmount.");
      }
    };
  }, []);

  useEffect(() => {
    if (interviews.length > 0) {
      const events = interviews.map((interview) => ({
        title: `${interview.position} @ ${interview.company}`,
        start: parseISO(
          interview.startTime ||
            interview.date ||
            interview.scheduledDate
        ),
        end: parseISO(
          interview.endTime || interview.date || interview.scheduledDate
        ),
        status: interview.status || "scheduled",
      }));
      setCalendarEvents(events);
    }
  }, [interviews]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };
  const applyFilters = () => fetchData(true);
  const clearFilters = () => {
    setFilters({ company: "", status: "", startDate: "", endDate: "" });
    fetchData();
  };
  const markAllAsRead = () => {
    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };
  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
    setDropdownOpen(false);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "scheduled":
        return "#34D399";
      case "pending":
        return "#FBBF24";
      case "rejected":
        return "#F87171";
      default:
        return "#60A5FA";
    }
  };
  const getStatusCounts = () => {
    const counts = {};
    interviews.forEach((interview) => {
      const status = interview.status || "Unknown";
      counts[status] = (counts[status] || 0) + 1;
    });
    return Object.values(counts);
  };
  const getStatusLabels = () => {
    const labels = new Set();
    interviews.forEach((interview) => {
      labels.add(interview.status || "Unknown");
    });
    return Array.from(labels);
  };
  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: getStatusColor(event.status),
      borderRadius: "6px",
      color: "white",
      border: "none",
      padding: "4px",
    },
  });

  return (
    <Layout>
      <div className="p-6 relative">
        {loading && <p>Loading dashboard...</p>}
        {error && <p className="text-red-500">{error}</p>}
        {!loading && !error && (
          <>
            {/* Notifications */}
            <div className="flex justify-between mb-4">
              <h1 className="text-2xl font-bold">Interview Dashboard</h1>
              <div className="relative">
                <button onClick={() => setDropdownOpen(!dropdownOpen)}>
                  <Bell className="w-6 h-6" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1">
                      {unreadCount}
                    </span>
                  )}
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-white shadow-lg rounded z-50">
                    <div className="flex justify-between px-3 py-2 border-b">
                      <span className="font-semibold">Notifications</span>
                      <button
                        onClick={clearNotifications}
                        className="text-xs text-red-500"
                      >
                        Clear all
                      </button>
                    </div>
                    <ul className="max-h-60 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <li className="px-3 py-2 text-gray-500">
                          No notifications
                        </li>
                      ) : (
                        notifications.map((n, i) => (
                          <li
                            key={i}
                            className={`px-3 py-2 border-b ${
                              !n.read ? "bg-yellow-50" : ""
                            }`}
                          >
                            <p className="font-medium">{n.title}</p>
                            <p className="text-sm">{n.message}</p>
                          </li>
                        ))
                      )}
                    </ul>
                    <div className="px-3 py-2 border-t text-center">
                      <button
                        onClick={markAllAsRead}
                        className="text-sm text-blue-500"
                      >
                        Mark all as read
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

          {/* User Profile */}
<div className="mb-4">
  <ProfileCard userData={userData} />
</div>


            {/* Filters */}
            <div className="mb-4 p-4 bg-white rounded shadow">
              <h2 className="font-semibold mb-3 flex items-center gap-2">
                <Filter className="w-5 h-5" /> Filter Interviews
              </h2>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

                <input
                  type="text"
                  name="company"
                  value={filters.company}
                  onChange={handleFilterChange}
                  placeholder="Company"
                  className="border px-3 py-2 rounded"
                />
                <input
                  type="text"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  placeholder="Status"
                  className="border px-3 py-2 rounded"
                />
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="border px-3 py-2 rounded"
                />
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="border px-3 py-2 rounded"
                />
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-blue-500 text-white rounded"
                >
                  Apply Filters
                </button>
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-gray-300 rounded"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

              <div className="p-4 bg-white rounded shadow text-center">
                <Briefcase className="text-blue-500 w-8 h-8 mb-2" />
                <p>Total Interviews: {stats.totalInterviews}</p>
              </div>
              <div className="p-4 bg-white rounded shadow text-center">
                <Layers className="text-green-500 w-8 h-8 mb-2" />
                <p>Total Rounds: {stats.totalRounds}</p>
              </div>
              <div className="p-4 bg-white rounded shadow text-center">
                <HelpCircle className="text-purple-500 w-8 h-8 mb-2" />
                <p>Total Questions: {stats.totalQuestions}</p>
              </div>
            </div>
{/* --- Subscription Section --- */}
<div className="my-6 p-4 bg-gray-100 rounded-md">
  <h2 className="text-xl font-bold mb-2">Upgrade Your Plan</h2>
  <p className="mb-4">Free users have limited AI usage. Upgrade to get more tokens.</p>
  <div className="flex gap-4">
    <SubscribeButton plan="basic" />
    <SubscribeButton plan="pro" />
  </div>
</div>

            {/* Table */}
            <div className="mt-8 bg-white p-4 rounded shadow overflow-x-auto">
  <h2 className="text-xl font-semibold mb-4">Interview Table</h2>
  <div className="min-w-[800px]">
    <InterviewTable interviews={interviews} />
  </div>
</div>


            {/* Timelines */}
            <div className="mt-8 space-y-6">

              <h2 className="text-xl font-semibold mb-4">
                Your Interview Timelines
              </h2>
              {interviews.map((interview) => (
  <div key={interview.id} className="mb-6 p-4 bg-white rounded shadow">

    <h3 className="text-lg font-semibold">
      {interview.position} @ {interview.company}
    </h3>

    {/* Resume upload/download */}
    <ResumeUpload interviewId={interview.id} />

    <TimelineView
      groupedTimeline={{ [interview.id]: timelines[interview.id] }}
    />
  </div>
))}
</div>
            {/* Analytics */}
            <div className="mt-8 bg-white shadow rounded p-6">
              <h2 className="text-xl font-semibold mb-6">
                📊 Analytics Dashboard
              </h2>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">

                <div className="bg-blue-100 p-4 rounded">
                  <p>Average Rounds per Interview</p>
                  <p className="text-2xl font-bold">
                    {(stats.totalRounds / (stats.totalInterviews || 1)).toFixed(
                      1
                    )}
                  </p>
                </div>
                <div className="bg-yellow-100 p-4 rounded">
                  <p>Avg Questions per Round</p>
                  <p className="text-2xl font-bold">
                    {(
                      stats.totalQuestions / (stats.totalRounds || 1)
                    ).toFixed(1)}
                  </p>
                </div>
                <div className="bg-green-100 p-4 rounded">
                  <p>Total Companies Interviewed</p>
                  <p className="text-2xl font-bold">
                    {Object.keys(stats.interviewsByCompany).length}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    Interviews by Status
                  </h3>
                  <Doughnut
                    data={{
                      labels: getStatusLabels(),
                      datasets: [
                        {
                          data: getStatusCounts(),
                          backgroundColor: [
                            "#34D399",
                            "#FBBF24",
                            "#F87171",
                            "#60A5FA",
                          ],
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      plugins: { legend: { position: "bottom" } },
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Company Chart */}
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">
                Interviews by Company
              </h2>
              <div className="max-w-sm mx-auto">
                <Doughnut
                  data={{
                    labels: Object.keys(stats.interviewsByCompany),
                    datasets: [
                      {
                        data: Object.values(stats.interviewsByCompany),
                        backgroundColor: [
                          "#36A2EB",
                          "#FF6384",
                          "#FFCE56",
                          "#4BC0C0",
                          "#9966FF",
                          "#FF9F40",
                        ],
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: { legend: { position: "bottom" } },
                  }}
                />
              </div>
            </div>

            {/* Calendar */}
           <div className="overflow-x-auto">
  <Calendar
    style={{ minHeight: 400, width: "100%" }}
    localizer={momentLocalizer(moment)}
    events={calendarEvents}
    startAccessor="start"
    endAccessor="end"
    eventPropGetter={eventStyleGetter}
    onSelectEvent={(event) => {
      alert(
        `Interview Details:\nPosition: ${event.title}\nStatus: ${event.status}\nStart: ${event.start.toLocaleString()}\nEnd: ${event.end.toLocaleString()}`
      );
    }}
  />
</div>

          </>
        )}
      </div>
      <ToastContainer />
    </Layout>
  );
}

export default InterviewBoard;
